import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';

import { WebDashboardShell } from '../../../components/layout/WebDashboardShell';
import {
  WebTabBarBridge,
  WebTabBarBridgeProvider,
} from '../../../components/layout/WebTabLayoutContext';
import { GlassTabBar } from '../../../components/ui/GlassTabBar';
import { useAppLayout } from '../../../hooks/useAppLayout';
import { colors } from '../../../core/theme/tokens';
import type { MainTabParamList } from '../../../core/navigation/types';
import { MAIN_TABS } from './mainTabConfig';
import { webOnlyFocusedTab } from './webOnlyFocusedTab';

const Tab = createBottomTabNavigator<MainTabParamList>();

function WebTabBar(props: React.ComponentProps<typeof GlassTabBar>) {
  const { sidebar } = useAppLayout();

  if (sidebar) {
    return <WebTabBarBridge props={props} />;
  }

  return <GlassTabBar {...props} />;
}

/** Web main app — sidebar nav ≥768px, bottom glass tab bar on compact viewports. */
export function MainTabNavigator() {
  const tabs = useMemo(
    () =>
      MAIN_TABS.map((tab) => ({
        ...tab,
        component: webOnlyFocusedTab(tab.component),
      })),
    [],
  );

  return (
    <WebTabBarBridgeProvider>
      <WebDashboardShell>
        <Tab.Navigator
          tabBar={(props) => <WebTabBar {...props} />}
          detachInactiveScreens
          screenOptions={{
            headerShown: false,
            sceneStyle: styles.scene,
            lazy: true,
            animation: 'none',
          }}
        >
          {tabs.map((tab) => (
            <Tab.Screen key={tab.name} name={tab.name} component={tab.component} />
          ))}
        </Tab.Navigator>
      </WebDashboardShell>
    </WebTabBarBridgeProvider>
  );
}

const styles = StyleSheet.create({
  scene: {
    flex: 1,
    backgroundColor: colors.background,
  },
});
